import React, { useContext } from 'react';
import styles from '../../../../styles/pages/DocumentationPage/components/DocTabs/PostLead.module.css';
import { LangContext } from '../../../../context/LangContext';
import { ThemeContext } from '../../../../context/ThemeContext';

const API_BASE_URL = (process.env.REACT_APP_BASE_API_URL || '').replace(/\/$/, '');
const translations = {
    en: {
        transactionTitle: 'Get a Transaction', balanceTitle: 'Get Available Balance',
        requestInfoTitle: 'Request Information', urlLabel: 'URL:', methodLabel: 'Method:',
        tokenLabel: 'Your Token:', loading: 'Loading...', error: 'Error', missing: 'Missing',
        pathParamsTitle: 'Path Parameters', requestParamsTitle: 'Request Parameters',
        requiredInteger: 'required, integer', requiredString: 'required, string', exampleLabel: 'Example:',
        lead_id: 'ID of a lead created by your affiliate account.',
        transaction_id: 'CRM transaction ID returned by the deposit or withdrawal API. The transaction must belong to this lead.',
        token: 'Integration token.',
        transactionDescription: 'Returns transaction details stored in CRM. No request body is needed.',
        balanceDescription: 'Returns the current available balance in USD from the platform. No request body is needed. If the platform is unavailable, the request fails instead of returning a stale local balance.',
        transactionFields: 'unique_name is returned only in affiliate transaction API responses. It is null when no name was provided. Of the optional creation fields, the lead sees only status; bank, comment, number, wallet and unique_name are internal details.',
        example: 'Request Example (cURL)', responses: 'Server Responses', success: 'Successful Request (200 OK)',
        notFound: 'Lead not found or does not belong to the affiliate (404 Not Found)',
        transactionNotFound: 'Transaction not found or does not belong to this lead (404 Not Found)',
        unauthorized: 'Invalid token or blocked affiliate (401 Unauthorized)',
        validation: 'Missing or invalid token parameter (422 Unprocessable Entity)',
    },
    ru: {
        transactionTitle: 'Получение транзакции', balanceTitle: 'Получение доступного баланса',
        requestInfoTitle: 'Информация о запросе', urlLabel: 'URL:', methodLabel: 'Метод:',
        tokenLabel: 'Ваш токен:', loading: 'Загрузка...', error: 'Ошибка', missing: 'Отсутствует',
        pathParamsTitle: 'Параметры пути', requestParamsTitle: 'Параметры запроса',
        requiredInteger: 'обязательный, integer', requiredString: 'обязательный, string', exampleLabel: 'Пример:',
        lead_id: 'ID лида, созданного вашим партнёрским аккаунтом.',
        transaction_id: 'CRM ID транзакции из ответа API создания депозита или вывода. Транзакция должна принадлежать этому лиду.',
        token: 'Интеграционный токен.',
        transactionDescription: 'Возвращает данные транзакции из CRM. Тело запроса не требуется.',
        balanceDescription: 'Возвращает текущий доступный баланс в USD с платформы. Тело запроса не требуется. Если платформа недоступна, запрос завершится ошибкой, а не вернёт устаревший локальный баланс.',
        transactionFields: 'unique_name возвращается только в ответах партнёрского API транзакций. Если название не было передано, возвращается null. Из необязательных полей создания лид видит только status; bank, comment, number, wallet и unique_name — внутренние данные.',
        example: 'Пример запроса (cURL)', responses: 'Ответы сервера', success: 'Успешный запрос (200 OK)',
        notFound: 'Лид не найден или не принадлежит партнёру (404 Not Found)',
        transactionNotFound: 'Транзакция не найдена или не принадлежит этому лиду (404 Not Found)',
        unauthorized: 'Неверный токен или партнёр заблокирован (401 Unauthorized)',
        validation: 'Токен не передан или имеет неверный тип (422 Unprocessable Entity)',
    },
};

export const GetPaymentInformation = ({ affiliateToken, loading, error, balance = false }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const themed = (name) => `${styles[name]} ${theme === 'dark' ? styles['dark' + name[0].toUpperCase() + name.slice(1)] || '' : ''}`;
    const endpoint = `${API_BASE_URL}/integration/lead/leads/{lead_id}/${balance ? 'balance' : 'transactions/{transaction_id}'}`;
    const exampleUrl = `${API_BASE_URL}/integration/lead/leads/123/${balance ? 'balance' : 'transactions/42'}?token=${encodeURIComponent(affiliateToken || 'YOUR_TOKEN')}`;
    const pathParameters = [{ name: 'lead_id', meta: t.requiredInteger, example: '123' }];
    if (!balance) {
        pathParameters.push({ name: 'transaction_id', meta: t.requiredInteger, example: '42' });
    }
    const parameterGroups = [
        { title: t.pathParamsTitle, parameters: pathParameters },
        { title: t.requestParamsTitle, parameters: [{ name: 'token', meta: t.requiredString, example: 'n8902n02ed902me02ec' }] },
    ];
    const responses = [
        { title: t.success, data: balance ? { lead_id: 123, available_balance: 250.5, assets: 'usd' } : {
            id: 42, lead_id: 123, amount: 250.5, assets: 'usd', status: 'success',
            created_at: '06-09-2026 12:30:00', unique_name: 'payment-123', action: 'deposit',
            bank: 'Example Bank', comment: 'Payment reference', number: '0012345678', wallet: null,
            date: '06-09-2026 12:30:00', updated_at: '06-09-2026 12:30:00',
        } },
        { title: t.notFound, data: {
            message: 'Lead not exists.', errors: [{ code: 'RESOURCE_NOT_FOUND', message: 'Lead not exists.' }],
        } },
        ...(!balance ? [{ title: t.transactionNotFound, data: {
            message: 'Transaction not exists.', errors: [{ code: 'RESOURCE_NOT_FOUND', message: 'Transaction not exists.' }],
        } }] : []),
        { title: t.unauthorized, data: { message: 'Unauthenticated.' } },
        { title: t.validation, data: {
            message: 'The given data was invalid.', errors: { token: 'The token field is required.' },
        } },
    ];

    return (
        <div className={themed('mainContainer')}>
            <h2 className={themed('title')}>{balance ? t.balanceTitle : t.transactionTitle}</h2>
            <div className={themed('card')}>
                <h3 className={themed('cardTitle')}>{t.requestInfoTitle}</h3>
                <div className={styles.infoItem}>
                    <span className={themed('infoLabel')}>{t.urlLabel}</span>
                    <span className={themed('infoValue')}>{endpoint}</span>
                </div>
                <div className={styles.infoItem}>
                    <span className={themed('infoLabel')}>{t.methodLabel}</span>
                    <span className={themed('infoValue')}>GET</span>
                </div>
                <div className={styles.infoItem}>
                    <span className={themed('infoLabel')}>{t.tokenLabel}</span>
                    <span className={themed('token')}>{loading ? t.loading : error ? t.error : affiliateToken || t.missing}</span>
                </div>
                <p className={themed('paramDescription')}>{balance ? t.balanceDescription : t.transactionDescription}</p>
            </div>
            {parameterGroups.map((group) => (
                <div key={group.title} className={themed('card')}>
                    <h3 className={themed('cardTitle')}>{group.title}</h3>
                    <ul className={styles.paramsList}>
                        {group.parameters.map(({ name, meta, example }) => (
                            <li key={name} className={themed('paramItem')}>
                                <div className={styles.paramName}>
                                    <span className={themed('paramKey')}>{name}</span>
                                    <span className={styles.paramMeta}>({meta})</span>
                                </div>
                                <p className={themed('paramDescription')}>
                                    {t[name]} {t.exampleLabel} <code className={themed('codeInline')}>{example}</code>
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
            <div className={themed('card')}>
                <h3 className={themed('cardTitle')}>{t.example}</h3>
                <pre className={styles.codeBlock}><code>{`curl --request GET \\
  --url '${exampleUrl}' \\
  --header 'Accept: application/json'`}</code></pre>
            </div>
            <div className={themed('card')}>
                <h3 className={themed('cardTitle')}>{t.responses}</h3>
                {!balance && <p className={themed('paramDescription')}>{t.transactionFields}</p>}
                {responses.map(({ title, data }) => (
                    <div key={title} className={themed('responseGroup')}>
                        <h4 className={themed('responseSubTitle')}>{title}</h4>
                        <pre className={styles.responseBlock}><code>{JSON.stringify(data, null, 2)}</code></pre>
                    </div>
                ))}
            </div>
        </div>
    );
};
